import React, { useMemo } from 'react';
import { FaCog } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../redux/reducers';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import { CommunityInvitePropertyType } from '../../../models/inviteService.model';
import { BasicInfo } from '../../../types/BasicInfo';
import {
  BaseTable,
  BaseTableBody,
  BaseTableDataRow,
  BaseTableHeader,
  BaseTableHeaderRow,
  BaseTableHelperText,
} from '../../components/tables/BaseTable';
import Button from '../../components/buttons/Button';

interface BirthdayEntry {
  id: number;
  name: string;
  birthDate: string;
  age: number;
  daysUntil: number;
}

export default function BirthdaysTab(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const community = useSelectedCommunity();
  const connections = useSelector((state: RootState) => state.connections);

  const isBirthdayRequested = useMemo(() => {
    if (!community?.inviteFields) return false;
    
    const requestedFields = [
      ...community.inviteFields.mandatoryFields,
      ...community.inviteFields.optionalFields,
    ];
    
    return requestedFields.includes(CommunityInvitePropertyType.BIRTHDATE);
  }, [community?.inviteFields]);

  const birthdayEntries = useMemo((): BirthdayEntry[] => {
    if (!isBirthdayRequested) return [];

    const entries = connections
      .filter((connection) => connection.fields[BasicInfo.BIRTHDATE])
      .map((connection) => {
        const birthDateString = connection.fields[BasicInfo.BIRTHDATE];
        const [day, month, year] = birthDateString.split('-').map(Number);
        if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day) || year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return null;
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();

        const currentYear = today.getFullYear();
        const nextBirthday = new Date(currentYear, month - 1, day);
        if (nextBirthday < today) {
          nextBirthday.setFullYear(currentYear + 1);
        }

        let age = today.getFullYear() - birthDate.getFullYear();
        const hasHadBirthdayThisYear = new Date(currentYear, month - 1, day) <= today;
        if (!hasHadBirthdayThisYear) age -= 1;
        
        const daysUntil = Math.ceil((nextBirthday.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        return {
          id: connection.dashboardMemberId,
          name: `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`,
          birthDate: birthDateString,
          age,
          nextBirthday,
          daysUntil,
        };
      })
      .filter((entry): entry is BirthdayEntry & { nextBirthday: Date } => entry !== null)
      .sort((a, b) => a.nextBirthday.getTime() - b.nextBirthday.getTime());

    return entries;
  }, [connections, isBirthdayRequested]);

  if (!isBirthdayRequested) {
    return (
      <div className="flex w-full items-center justify-center">
        <div className="max-w-md rounded-lg bg-secondary-100 p-8 text-center">
          <div className="mb-4">
            <img 
              src="/assets/figures/drop_sad.svg" 
              alt="sad drop" 
              className="mx-auto h-16 w-16"
            />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-primary-900">
            {t('page.agenda.birthdays.notEnabled.title')}
          </h3>
          <p className="mb-4 text-gray-600">
            {t('page.agenda.birthdays.notEnabled.description')}
          </p>
          <Button
            variant="primary"
            onClick={() => navigate('/settings/requested-data')}
            className="inline-flex items-center gap-2"
          >
            <FaCog className="h-4 w-4" />
            {t('page.agenda.birthdays.notEnabled.button')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-primary-900">
          {t('page.agenda.birthdays.title')}
        </h2>
        <p className="text-gray-600">
          {t('page.agenda.birthdays.subtitle', { count: birthdayEntries.length })}
        </p>
      </div>

      <BaseTable>
        <BaseTableHeader>
          <BaseTableHeaderRow>
            <th className="py-3 text-left font-semibold">
              {t('page.agenda.birthdays.table.name')}
            </th>
            <th className="py-3 text-left font-semibold">
              {t('page.agenda.birthdays.table.birthday')}
            </th>
            <th className="py-3 text-left font-semibold">
              {t('page.agenda.birthdays.table.age')}
            </th>
            <th className="py-3 text-left font-semibold">
              {t('page.agenda.birthdays.table.daysUntil')}
            </th>
          </BaseTableHeaderRow>
        </BaseTableHeader>
        <BaseTableBody>
          {birthdayEntries.length === 0 ? (
            <BaseTableHelperText>
              {t('page.agenda.birthdays.table.noBirthdays')}
            </BaseTableHelperText>
          ) : (
            birthdayEntries.map((entry) => (
              <BaseTableDataRow key={entry.id}>
                <td className="py-3 font-medium">{entry.name}</td>
                <td className="py-3">{entry.birthDate}</td>
                <td className="py-3">
                  {t('page.agenda.birthdays.table.ageValue', { old_age: entry.age, new_age: entry.age + 1 })}
                </td>
                <td className="py-3">{entry.daysUntil}</td>
              </BaseTableDataRow>
            ))
          )}
        </BaseTableBody>
      </BaseTable>
    </div>
  );
}